import type { CSSProperties } from 'react';

export const platformRegisterPalette = {
  textMuted: '#516075',
  borderDefault: '#D6D6E1',
  borderSoft: '#E4E7EC',
  accent: '#24A2B5',
  accentDark: '#1B7A87',
  accentSoft: '#E8F6F8',
  surfaceAlt: '#F7F9FC',
  headerBackground: '#F4F7FB',
  hoverBackground: '#F1FAFB',
  selectedBackground: '#EEF8FA',
  selectedHoverBackground: '#E6F5F8',
  ink: '#24303F',
};

export const platformColorRoles = {
  text: platformRegisterPalette.ink,
  textMuted: platformRegisterPalette.textMuted,
  primaryTeal: platformRegisterPalette.accent,
  teal: platformRegisterPalette.accentDark,
  border: platformRegisterPalette.borderDefault,
  borderSoft: platformRegisterPalette.borderSoft,
  focus: platformRegisterPalette.accentDark,
};

export const platformRadius = {
  panel: 8,
  surface: 16,
  mini: 10,
  pill: 999,
};

export const platformSpace = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
};

export const platformMotion = {
  stateFast: '120ms ease',
  stateStandard: '160ms ease',
};

export const platformShadow = {
  overlay: '0 18px 38px rgba(24, 23, 36, 0.12)',
  decisionSurface: '0 16px 34px rgba(24, 23, 36, 0.06)',
  dialog: '0 24px 60px rgba(24, 23, 36, 0.18)',
};

export const platformTypography = {
  titlePage: {
    color: platformColorRoles.text,
    fontWeight: 700,
  },
  titlePrimary: {
    color: platformColorRoles.text,
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.25,
  },
  text: {
    color: platformColorRoles.text,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  textBold: {
    color: platformColorRoles.text,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  textLink: {
    color: platformColorRoles.teal,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
    textDecoration: 'none',
  },
  label: {
    color: platformColorRoles.textMuted,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.04em',
    lineHeight: 1.2,
    textTransform: 'uppercase' as const,
  },
  pill: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1,
  },
};

export const platformSurfaceStyle: CSSProperties = {
  border: `1px solid ${platformColorRoles.border}`,
  borderRadius: platformRadius.surface,
  background: '#FFFFFF',
  boxShadow: 'none',
};

export const platformNestedSurfaceStyle: CSSProperties = {
  border: `1px solid ${platformColorRoles.borderSoft}`,
  borderRadius: platformRadius.surface,
  background: '#FFFFFF',
  boxShadow: 'none',
};

export const platformMiniCardStyle: CSSProperties = {
  border: `1px solid ${platformColorRoles.borderSoft}`,
  borderRadius: platformRadius.mini,
  background: '#FFFFFF',
  boxShadow: 'none',
};

// Explicit CSS units keep panel corners identical in React styles and MUI sx.
export const platformPanelSurfaceStyle: CSSProperties = {
  ...platformSurfaceStyle,
  borderRadius: `${platformRadius.panel}px`,
};

export const platformPanelNestedSurfaceStyle: CSSProperties = {
  ...platformNestedSurfaceStyle,
  borderRadius: `${platformRadius.panel}px`,
};

export const platformPanelMiniCardStyle: CSSProperties = {
  ...platformMiniCardStyle,
  borderRadius: `${platformRadius.panel}px`,
};

export const platformFocusRingStyle: CSSProperties = {
  outline: `2px solid ${platformColorRoles.focus}`,
  outlineOffset: 2,
};

export const platformInsetFocusRingStyle: CSSProperties = {
  outline: `2px solid ${platformColorRoles.focus}`,
  outlineOffset: -2,
};

export const platformAccessButtonStyle: CSSProperties = {
  ...platformTypography.textBold,
  borderRadius: platformRadius.pill,
  textTransform: 'none',
  color: platformColorRoles.teal,
  borderColor: platformColorRoles.teal,
  backgroundColor: '#FFFFFF',
  boxShadow: 'none',
  flexShrink: 0,
  minWidth: 76,
  padding: '0.42rem 0.95rem',
  whiteSpace: 'nowrap',
  transition: `background-color ${platformMotion.stateStandard}, border-color ${platformMotion.stateStandard}, color ${platformMotion.stateStandard}`,
};

export const platformPrimaryButtonStyle: CSSProperties = {
  ...platformTypography.textBold,
  borderRadius: platformRadius.pill,
  textTransform: 'none',
  padding: '0.42rem 0.95rem',
  minWidth: 76,
  whiteSpace: 'nowrap',
  borderColor: platformColorRoles.primaryTeal,
  color: '#FFFFFF',
  backgroundColor: platformColorRoles.primaryTeal,
  boxShadow: 'none',
  flexShrink: 0,
  transition: `background-color ${platformMotion.stateStandard}, border-color ${platformMotion.stateStandard}, color ${platformMotion.stateStandard}`,
};

export const platformPillRoleStyle: CSSProperties = {
  ...platformTypography.pill,
  borderRadius: platformRadius.pill,
};

export const platformSelectorPillStyle: CSSProperties = {
  ...platformPillRoleStyle,
  alignItems: 'center',
  backgroundColor: '#FFFFFF',
  border: `1px solid ${platformColorRoles.border}`,
  boxShadow: 'none',
  color: platformColorRoles.text,
  display: 'inline-flex',
  minHeight: 32,
  minWidth: 0,
  padding: '0.36rem 0.78rem',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  transition: `background-color ${platformMotion.stateStandard}, border-color ${platformMotion.stateStandard}, color ${platformMotion.stateStandard}`,
};

export const platformSelectorPillSelectedStyle: CSSProperties = {
  ...platformSelectorPillStyle,
  backgroundColor: platformRegisterPalette.accentSoft,
  borderColor: platformColorRoles.teal,
  color: platformColorRoles.teal,
};

export const platformSelectorPillActionStyle: CSSProperties = {
  ...platformSelectorPillStyle,
  color: platformColorRoles.teal,
};

export const platformFilterSelectProps = {
  MenuProps: {
    slotProps: {
      paper: {
        style: {
          maxHeight: 360,
        },
      },
      list: {
        sx: {
          display: 'block',
          padding: '4px 0',
          '& .MuiMenuItem-root': {
            ...platformTypography.text,
            display: 'flex',
            justifyContent: 'flex-start',
            minHeight: 36,
            padding: '8px 16px',
            textAlign: 'left',
            whiteSpace: 'nowrap',
          },
        },
      },
    },
  },
};

export const createPlatformDataGridStyles = (
  gridClassName: string,
  selectedRowClassName = 'Mui-selected'
): Record<string, unknown> => ({
  [`.${gridClassName}`]: {
    border: 0,
    background: '#FFFFFF',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: platformRegisterPalette.headerBackground,
      borderBottom: `1px solid ${platformColorRoles.borderSoft}`,
    },
    '& .MuiDataGrid-columnHeader': {
      alignItems: 'center',
      display: 'flex',
      padding: '0 10px',
    },
    '& .MuiDataGrid-row': {
      alignItems: 'center',
    },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within, &.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell:focus, &.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within, &.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell:focus-within':
      {
        outline: 'none !important',
      },
    '& .MuiDataGrid-columnHeader:focus-visible, & .MuiDataGrid-cell:focus-visible, &.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-visible, &.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell:focus-visible':
      {
        outline: `2px solid ${platformColorRoles.focus} !important`,
        outlineOffset: -2,
      },
    '& .MuiDataGrid-columnHeader:has(:focus-visible), & .MuiDataGrid-cell:has(:focus-visible), &.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-columnHeader:has(:focus-visible), &.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell:has(:focus-visible)':
      {
        outline: `2px solid ${platformColorRoles.focus} !important`,
        outlineOffset: -2,
      },
    '& .MuiDataGrid-columnHeaderTitle': {
      ...platformTypography.label,
    },
    '& .MuiDataGrid-cell': {
      ...platformTypography.text,
      display: 'flex',
      borderBottom: `1px solid ${platformColorRoles.borderSoft}`,
      alignItems: 'center',
      padding: '0 10px',
    },
    '& .MuiDataGrid-cellContent': {
      alignItems: 'center',
      display: 'flex',
      minHeight: '100%',
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: platformRegisterPalette.hoverBackground,
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: `1px solid ${platformColorRoles.borderSoft}`,
    },
    [`& .${selectedRowClassName}`]: {
      backgroundColor: `${platformRegisterPalette.selectedBackground} !important`,
      boxShadow: `inset 0 1px 0 ${platformColorRoles.teal}, inset 0 -1px 0 ${platformColorRoles.teal}`,
    },
    [`& .${selectedRowClassName}:hover`]: {
      backgroundColor: `${platformRegisterPalette.selectedHoverBackground} !important`,
    },
    [`& .${selectedRowClassName} .MuiDataGrid-cell`]: {
      borderBottom: `1px solid ${platformColorRoles.borderSoft}`,
    },
  },
});

export const createPlatformFilterFieldStyles = (
  filterClassName: string
): Record<string, unknown> => ({
  [`.${filterClassName}`]: {
    '& .MuiInputBase-root': {
      minHeight: 42,
    },
    '& .MuiInputBase-input': {
      ...platformTypography.text,
      paddingLeft: '12px',
    },
    '& .MuiOutlinedInput-input': {
      paddingLeft: '12px',
    },
    '& .MuiSelect-select': {
      alignItems: 'center',
      display: 'flex',
      minHeight: 'unset',
      overflow: 'hidden',
      paddingBottom: '10px',
      paddingLeft: '12px !important',
      paddingRight: '32px !important',
      paddingTop: '10px',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .MuiInputLabel-root': {
      fontSize: 13,
      left: 2,
    },
  },
});
